import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'

import { usePairAgeFormatted } from '@root/entities/pair/hooks'
import { PairIcon } from '@root/entities/pair/ui/common/pair-icon'
import { getXString } from '@root/entities/socials/model/socials'
import {
  justLanded,
  pairArrivalsAtom,
} from '@root/features/realtime/model/pair-arrivals'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'
import { useOptionalAtom } from '@root/shared/lib'
import { useCurrentDate } from '@root/shared/lib/react'

import { boardDisplayAtom } from '../../model/board-display-atom'
import {
  ageTone,
  bondingTone,
  bondingTurn,
  changeSlot,
  crown,
  edgeMark,
  shareTone,
  signedTone,
} from '../../model/pulse-readings'

import { toneVar } from './tone-var'

import type { EdgeMark, Tone } from '../../model/pulse-readings'
import type { PairMetrics, PairModel } from '@root/entities/pair'
import type BigNumber from 'bignumber.js'

type Props = {
  pair: PairModel
  statsPeriod: PairMetrics.MetricsSpan
}

/**
 * One row of the V2 board, class for class with the design prototype so the
 * stylesheet lifted from it applies unchanged.
 *
 * Source: https://claude.ai/artifact/5DHS184wUvyGV3PGQTbvLS
 */
export const PulseV2Row: React.FC<Props> = memo(({ pair, statsPeriod }) => {
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [liquidityUsd] = useAtom(pair.liquidityUsd)
  const [holdersCount] = useAtom(pair.holdersCount)
  const [audit] = useAtom(pair.audit)
  const [top10holding] = useAtom(pair.top10holding)
  const [snipers] = useAtom(pair.snipers)
  const [insiders] = useAtom(pair.insiders)
  const [bundlers] = useAtom(pair.bundlers)
  const [creatorStats] = useAtom(pair.creatorStats)
  const [bonding] = useAtom(pair.bondingPercentage)
  const [athMarketCap] = useAtom(pair.athMarketCap)
  const [totalFees] = useAtom(pair.fees.totalFees)
  const [socials] = useAtom(pair.quote.socials)
  const [chartMinutes] = useAtom(pair.priceChartMinutes)
  const [chartSeconds] = useAtom(pair.priceChartSeconds)
  const [devSold] = useAtom(pair.devSoldAllAtom)
  const [{ fields }] = useAtom(boardDisplayAtom)
  const [position] = useAtom(pair.position)
  const pnl = useOptionalAtom(position?.unrealizedPnlUsd)

  const [volume] = useAtom(pair.stats[statsPeriod].volumeUsd)
  const [txs] = useAtom(pair.stats[statsPeriod].transactions)
  const [makers] = useAtom(pair.stats[statsPeriod].makers)
  const [priceChange] = useAtom(pair.stats[statsPeriod].priceChange)
  const [netVolume] = useAtom(pair.stats[statsPeriod].netVolumeInUsd)

  const currentDate = useCurrentDate({ updateInterval: 'second' })
  const [arrivedAt] = useAtom(
    (ctx) => ctx.spy(pairArrivalsAtom).get(pair.address),
    [pair.address],
  )
  const landed = justLanded(arrivedAt, currentDate.getTime())
  const { formatted: age } = usePairAgeFormatted(pair.createdAt)
  const ageSeconds = pair.createdAt
    ? (currentDate.getTime() - pair.createdAt.getTime()) / 1000
    : undefined

  const change = plain(priceChange)
  const slot = changeSlot(pnl ? pnl.pnlUsd.toNumber() : undefined, change)
  const held = slot.kind === 'position'
  const mark = edgeMark(held, landed)
  const net = plain(netVolume)
  const record = crown(creatorStats?.totalPools, creatorStats?.bondedPools)
  const turn = bondingTurn(bonding)
  const spark = sparkline(chartMinutes) ?? sparkline(chartSeconds)
  const x = socials.x ? getXString(socials.x) : undefined
  const telegram =
    typeof socials.telegram === 'string' ? socials.telegram : undefined

  return (
    <article
      className="rw"
      /* Just landed: a green wash for two seconds, so a row that appears
         while the column is being read is seen to have appeared. */
      style={landed ? { background: 'rgba(70,184,125,0.05)' } : undefined}
    >
      {/* The side accent carries whichever matters more. A held position keeps
          it: it is the reason to find the row again, and it outlasts the two
          seconds the arrival is worth. */}
      <EdgeBar mark={mark} />

      <div className="top">
        <div className="avw">
          {fields.bonding ? (
            <BondingRing turn={turn} tone={bondingTone(bonding)} />
          ) : undefined}
          <div className="av">
            {/* No "add a logo" tile here. That placeholder is an invitation to
                the token's own creator, and on a column of new pairs it puts a
                clickable promo on most rows, a thumb's width from the buy
                button. A token without a picture gets its initial instead. */}
            <PairIcon
              pair={pair}
              size={64}
              withPreview
              withPremiumFallback={false}
              /* The icon draws its own outline and insets the picture by 2px
                 to make room for it, on a radius of its own. With the curve's
                 ring outside that, a tile wore three edges at three radii,
                 none of them concentric. The ring is the only edge here, so
                 the picture fills the tile. */
              outlined={false}
            />
          </div>
        </div>

        <div className="bd">
          <div className="ln1">
            <span className="tk">{pair.quote.ticker}</span>
            <span className="nm">{pair.quote.title}</span>
            {/* The one event worth a word instead of a number. `devSoldAllAtom`
                is true only when the creator's last transaction was a sell and
                their balance is now nothing — a creator who never held does not
                read as having sold. */}
            {devSold ? (
              <span
                className="flag"
                style={{
                  background: 'rgba(225,92,92,0.13)',
                  color: 'var(--down)',
                }}
              >
                Dev sold
              </span>
            ) : undefined}
            <span className="vm">
              <i className="lb">V</i>
              <b className="mono">{money(plain(volume))}</b>
              <i className="lb">MC</i>
              <b className="mono mc">{money(plain(marketCapUsd))}</b>
              {/* Holding it: the reader's own number takes the place of the
                  market's. The cap stays — it is what every other row shows
                  and what the two are compared by. */}
              <b
                className="mono chg"
                style={{ color: toneVar(signedTone(slot.value)) }}
                title={
                  held
                    ? 'What this position is up or down, unrealised'
                    : undefined
                }
              >
                {slot.kind === 'position'
                  ? signedMoney(slot.value)
                  : percent(slot.value)}
              </b>
            </span>
          </div>

          <div className="ln2">
            <span className="cl">
              <svg className="io" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8.6" />
                <path d="M12 7v5.2l3.4 2" />
              </svg>
              <b
                className="mono"
                style={{ color: toneVar(ageTone(ageSeconds)) }}
              >
                {age ?? '—'}
              </b>
            </span>

            <span className="soc" hidden={!fields.socials}>
              {x ? (
                <a href={x} target="_blank" rel="noreferrer" aria-label="X">
                  <svg className="is" viewBox="0 0 24 24">
                    <path d="M17.3 3h3.3l-7.2 8.2L21 21h-5.6l-4.4-5.7L5.9 21H2.6l7.5-8.6L2.5 3h5.7l4.1 5.4L17.3 3Zm-1.2 16h1.8L7.9 4.8H6L16.1 19Z" />
                  </svg>
                </a>
              ) : undefined}
              {telegram ? (
                <a
                  href={telegram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Telegram"
                >
                  <svg className="is" viewBox="0 0 24 24">
                    <path d="M21.6 4.4 3.2 11.5c-1 .4-1 1.7.1 2l4.1 1.3 1.6 5c.3.9 1.4 1 2 .3l2.2-2.4 4.3 3.1c.8.6 1.9.2 2.1-.8l3-13.5c.2-1-.8-2-1-2.1ZM9.9 14.7l7.9-6.5-8.6 5.6-.1.1.8.8Z" />
                  </svg>
                </a>
              ) : undefined}
              {socials.website ? (
                <a
                  href={socials.website}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Website"
                >
                  <svg className="io" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="8.6" />
                    <path d="M3.6 9.6h16.8M3.6 14.4h16.8" />
                    <path d="M12 3.4c-2.3 2.3-3.4 5.1-3.4 8.6s1.1 6.3 3.4 8.6c2.3-2.3 3.4-5.1 3.4-8.6S14.3 5.7 12 3.4Z" />
                  </svg>
                </a>
              ) : undefined}
            </span>

            <span className="cl fee">
              <i className="lb">F</i>
              <b className="mono">{trim(plain(totalFees))}</b>
            </span>
            {fields.netBought ? (
              <span className="cl">
                <i className="lb">NB</i>
                <b className="mono" style={{ color: toneVar(signedTone(net)) }}>
                  {signedMoney(net)}
                </b>
              </span>
            ) : undefined}
            <span className="cl">
              <i className="lb">TX</i>
              <b className="mono">{count(txs)}</b>
            </span>
            {spark ? (
              <svg
                className="spark"
                viewBox="0 0 62 16"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polyline
                  points={spark}
                  fill="none"
                  stroke={toneVar(signedTone(change))}
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity="0.8"
                />
              </svg>
            ) : undefined}
          </div>

          <div className="ln3">
            <button
              className="caw"
              type="button"
              title={pair.quote.address}
              hidden={!fields.address}
            >
              <svg className="io" viewBox="0 0 24 24">
                <rect x="9" y="9" width="11" height="11" rx="2.5" />
                <path d="M15 6.5V6a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 6v6.5A2.5 2.5 0 0 0 6 15h.5" />
              </svg>
              <b className="mono">{shortAddress(pair.quote.address)}</b>
            </button>

            <span className="cl">
              <svg className="is" viewBox="0 0 24 24">
                <path d="M12 5.8a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6Zm0 7.8c3.5 0 6.2 1.8 6.2 4v1.6H5.8v-1.6c0-2.2 2.7-4 6.2-4Z" />
              </svg>
              <b className="mono">{count(holdersCount)}</b>
            </span>
            <span className="cl mk">
              <svg className="io" viewBox="0 0 24 24">
                <path d="M4 9h13l-3.2-3.2M20 15H7l3.2 3.2" />
              </svg>
              <b className="mono">{count(makers)}</b>
            </span>
            <span className="cl drop">
              <svg className="is" viewBox="0 0 24 24">
                <path d="M12 3s5.8 6.3 5.8 10A5.8 5.8 0 0 1 6.2 13C6.2 9.3 12 3 12 3Z" />
              </svg>
              <b className="mono">{money(plain(liquidityUsd))}</b>
            </span>
            {record && fields.creator ? (
              <span
                className="cl crown"
                title="Pools this creator graduated, of all they launched"
              >
                <svg className="is" viewBox="0 0 24 24">
                  <path d="M3 8.2 6.8 12 12 4.6 17.2 12 21 8.2V18a1.4 1.4 0 0 1-1.4 1.4H4.4A1.4 1.4 0 0 1 3 18V8.2Z" />
                </svg>
                <b className="mono" style={{ color: toneVar(record.tone) }}>
                  {record.text}
                </b>
              </span>
            ) : undefined}
          </div>
        </div>
      </div>

      <div className="pills" hidden={!fields.risk}>
        <Share
          label="Dev"
          value={plain(audit.checks.ownerTokenBalance.percent)}
        />
        <Share label="Snip" value={plain(snipers.holding)} />
        <Share label="T10" value={plain(top10holding)} />
        <Share label="Ins" value={plain(insiders.holding)} className="ins" />
        <span className="pl bundle">
          <svg className="io" viewBox="0 0 24 24">
            <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 0 0-5.7-5.7l-1 1" />
            <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 0 0 5.7 5.7l1-1" />
          </svg>
          <b
            className="mono"
            style={{ color: toneVar(shareTone(plain(bundlers.holding))) }}
          >
            {percent(plain(bundlers.holding), false)}
          </b>
        </span>
        <span className="pl ath">
          <i>ATH</i>
          <b className="mono">{money(plain(athMarketCap))}</b>
        </span>

        {/* The real thing: quotes, signs and buys, with the amount the
            terminal already keeps for this section. */}
        <QuickBuyButton
          className="buy mbuy"
          pair={pair}
          section="exploreFeeds"
          withAmount
          withIcon
        />
      </div>
    </article>
  )
})

const Share: React.FC<{
  label: string
  value: number | undefined
  className?: string
}> = ({ label, value, className }) => (
  <span className={className ? `pl ${className}` : 'pl'}>
    <i>{label}</i>
    <b className="mono" style={{ color: toneVar(shareTone(value)) }}>
      {percent(value, false)}
    </b>
  </span>
)

/**
 * The last of the pair's minute candles, drawn to the box the design gives it.
 * A pair minutes old has one or two candles and nothing to draw, so it gets no
 * line rather than a flat one pretending to be a trend.
 */
const sparkline = (entries: ReadonlyArray<unknown>): string | undefined => {
  const closes = entries
    .map((entry) => {
      const price = (entry as { price?: { close?: unknown } }).price?.close
      const value = Number(price)
      return Number.isFinite(value) ? value : undefined
    })
    .filter((value): value is number => value !== undefined)
    .slice(-24)

  if (closes.length < 3) {
    return undefined
  }

  const low = Math.min(...closes)
  const high = Math.max(...closes)
  const range = high - low || 1
  const step = 62 / (closes.length - 1)

  return closes
    .map(
      (value, index) =>
        `${(index * step).toFixed(1)},${((1 - (value - low) / range) * 13 + 1.5).toFixed(1)}`,
    )
    .join(' ')
}

/** The readings arrive as BigNumber or number depending on their source. */
const plain = (value: BigNumber | number | undefined): number | undefined => {
  if (value === undefined) {
    return undefined
  }
  return typeof value === 'number' ? value : value.toNumber()
}

const money = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  const units: Array<[number, string]> = [
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ]
  for (const [limit, suffix] of units) {
    if (Math.abs(value) >= limit) {
      return `$${trimZeros((value / limit).toFixed(2))}${suffix}`
    }
  }
  return Math.abs(value) >= 1
    ? `$${value.toFixed(0)}`
    : `$${trimZeros(value.toFixed(3))}`
}

const signedMoney = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value) || value === 0) {
    return '—'
  }
  return `${value > 0 ? '+' : '-'}${money(Math.abs(value))}`
}

/** Whole percents once past one, so a column of them keeps the same width. */
const percent = (value: number | undefined, signed = true): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  const body =
    Math.abs(value) >= 1
      ? Math.round(value)
      : Number(trimZeros(value.toFixed(1)))
  return `${signed && value > 0 ? '+' : ''}${body}%`
}

const count = (value: number | undefined): string => {
  if (value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  return value >= 1000 ? `${trimZeros((value / 1000).toFixed(2))}K` : `${value}`
}

/** Fees are read in SOL, where the interesting digits are the small ones. */
const trim = (value: number | undefined): string =>
  value === undefined || !Number.isFinite(value)
    ? '—'
    : trimZeros(value.toFixed(4))

const trimZeros = (value: string): string =>
  value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value

const shortAddress = (address: string): string =>
  address.length > 9 ? `${address.slice(0, 4)}…${address.slice(-4)}` : address

/**
 * The bonding curve, as a ring around the token's tile.
 *
 * Stroked along the path rather than swept by angle. A conic gradient measures
 * the angle from the centre, and on a square the corners sit at 45, 135, 225
 * and 315 degrees — so a quarter of the sweep is not a quarter of the edge.
 * The arc crawled along the flat sides and raced through the corners, which is
 * what made it look crooked and put the gap of a nearly-full ring in the wrong
 * place. `pathLength` normalises the perimeter to 100, so the dash is the
 * percentage, exactly.
 */
const RING = {
  /* The stroke is centred on the path, so the rect is inset by half of it. */
  box: 68,
  width: 2,
  radius: 7,
  /* Where the top edge's midpoint falls along the path, which starts after
     the top-left corner: (68 - 2 - 2 * 7) / 2 = 26 of a 252 perimeter. */
  startAtTop: 10.3,
} as const

const BondingRing: React.FC<{ turn: number; tone: Tone }> = memo(
  ({ turn, tone }) => {
    const inset = RING.width / 2
    const side = RING.box - RING.width

    return (
      <svg
        className="ring"
        viewBox={`0 0 ${RING.box} ${RING.box}`}
        aria-hidden="true"
      >
        <rect
          x={inset}
          y={inset}
          width={side}
          height={side}
          rx={RING.radius}
          fill="none"
          stroke="rgba(255,255,255,.10)"
          strokeWidth={RING.width}
        />
        {turn > 0 ? (
          <rect
            x={inset}
            y={inset}
            width={side}
            height={side}
            rx={RING.radius}
            fill="none"
            stroke={toneVar(tone)}
            strokeWidth={RING.width}
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${(turn * 100).toFixed(1)} 100`}
            strokeDashoffset={-RING.startAtTop}
          />
        ) : undefined}
      </svg>
    )
  },
)

/** The 2px mark down the side of a row, when the row has earned one. */
const EdgeBar: React.FC<{ mark: EdgeMark }> = memo(({ mark }) => {
  if (!mark) {
    return undefined
  }

  return (
    <span
      className="edge"
      style={{ background: mark === 'held' ? 'var(--accent)' : 'var(--up)' }}
    />
  )
})

/*
 * There is no migrated flag on this row, and that is deliberate.
 *
 * `isMigrated` does not mean "graduated": pools still bonding carry a
 * migration record too — it names the launchpad they came from, not a move
 * they have made — so every Final stretch row read as migrated. And the only
 * venue in reach on such a row is `migratedFromProvider`, which is where the
 * token started, so naming it labelled every pool "Pump Fun" as though that
 * were its destination.
 *
 * The same precedence costs that column its bonding ring: migration is
 * checked before the bonding curve, so a cooking pool reports no percentage
 * and draws an empty ring. That is in the shared pair model, older than this
 * screen and visible on the old board too.
 */
